import { ElementRef } from "@angular/core";
import { WellSegmentModel } from "../../new-casing-plan/models/casing-plan.model";
import { hasCasing, hasBorehole, hasCement } from "../../new-casing-plan/models/casing-plan-functions.model";
import { CasinPlanDiagramDirectionTypes } from "./casing-plan-diagram-direction-types.enum";

/**
 * constants for schematic rendering
 * */
export class SchematicConstants {
    static schematicWellWidth: number = 20;
    static schematicTop: number = 3;            //room for black ground rectangle
    static schematicBottom: number = 750;
    static schematicLeft: number = 10;
    static schematicRight: number = 740;
}

/**
 * main model for drawing, contains ref to CTX
 * */
export class CanvasModel {
    _canvasRef: ElementRef;
    canvasHeight: number;
    canvasWidth: number;
    ctx: CanvasRenderingContext2D;
    verticalFeetToPixels: number;
    diagonalFeetToPixels: number;
    get canvasRef() {
        return this._canvasRef;
    }
    set canvasRef(value: ElementRef) {
        this._canvasRef = value;
        this.ctx = this._canvasRef.nativeElement.getContext('2d');
        this.canvasHeight = this._canvasRef.nativeElement.height;
        this.canvasWidth = this._canvasRef.nativeElement.width;
    }
}

/**
 * generic class for canvas line drawing
 * */
export class CanvasLineModel{
    strokeStyle: string;
    strokeWidth: number;
    fromToLeftCement: CanvasXY[] = [];
    fromToRightCement: CanvasXY[] = [];
}

/**
 * kick point model
 * */
export class CanvasKickPointModel {
    firstKickPointFt: number;       //first kick point in feet
    _canvasXY: CanvasXY = new CanvasXY();
    set canvasXY(xy: { X: number, Y: number }) {
        this._canvasXY.X = xy.X;
        this._canvasXY.Y = xy.Y;
    }
    get canvasXY() {
        return this._canvasXY;
    }
    /**
     * Way to vertically offset the kick point based on number of strings above and below the kick point
     *  allow for better label spacing if there is an abundance above or below
     * @param numSegments
     * @param topStringCount
     */
    setDiagonalOffsetCanvasXY(numSegments: number, topStringCount: number): void {
        //numn segments
        let length = numSegments;
        //temp placeholder to manipulate top string count
        let calcTopString = topStringCount;

        //make sure it's not 0 or length of wellSegments
        if (calcTopString == length) calcTopString -= 1;
        if (calcTopString == 0) calcTopString += 1;

        //cal multiplier (.8 shrink top becuase it has more label room then diagonal)
        let topMultiplier = (calcTopString / length) * .8;

        //create values for call
        let newCanvasX = this._canvasXY.X
        let newCanvasY = (SchematicConstants.schematicBottom - SchematicConstants.schematicTop) * topMultiplier;

        //set new camvas kick point
        this.canvasXY = { X: newCanvasX, Y: newCanvasY };
    }
    setFirstKickPoint(wellSegments: Array<WellSegmentModel>): number {
        if (wellSegments) {
            for (let segment of wellSegments) {
                if (segment.hasAngles) {
                    let top: number = (segment.casingTop != null) ? segment.casingTop : segment.boreholeTop;
                    let bottom: number = (segment.casingBottom != null) ? segment.casingBottom : segment.boreholeBottom;
                    if (top != null && bottom != null) {
                        this.firstKickPointFt = (bottom - top) / 2; //exact position doesn't matter much, just place it in the middle of the segment with angles
                        return this.firstKickPointFt;
                    }
                }
            }
            return undefined;
        } else {
            return undefined;
        }
    }
}

/**
 * main model that creates all schematic location data
 * */
export class CanvasSchematicBoreHoleModel extends CanvasLineModel {
    _canvasModel: CanvasModel;
    _canvasKickPointModel: CanvasKickPointModel;
    _segment: WellSegmentModel;
    startsBeforeKickPoint: boolean;
    endsAfterKickPoint: boolean;
    startsAfterKickPoint: boolean;
    boreholeTop: number;
    boreHoleBottom: number;
    kickpoint: number;
    topSegment: boolean;
    toKickPoint: boolean;
    toFromKickPoint: boolean;
    bottomSegment: boolean;
    segmentIndex: number;
    strokeStyleCement = "#888888";
    lineWidthCement = 20;
    strokeStyleCasing = "#000000";
    lineWidthCasing = 2;
    fromToLeftCement: CanvasXYOrientation[] = [];
    fromToRightCement: CanvasXYOrientation[] = [];
    fromToRightCasing: CanvasXYOrientation[] = [];
    fromToLeftCasing: CanvasXYOrientation[] = [];
    quadraticCurveHeight: number = 50;
    quadraticCurveWidth: number = 15;
    quadraticCurveQuadY: number = 20;
    hasShoe: boolean;
    shoeLocations: CanvasXYShoe[] = [];
    labelLocations: CanvasSchematicStringLabel[] = [];          //houses BOTH left and right labels for each string
    finalStringCasingCloseLocations: CanvasXYOrientation[] = [];
    finalStringShoeLocations: CanvasXYOrientation[] = [];
    shoeHW:number = 20;
    constructor(canvasModel: CanvasModel, segment: WellSegmentModel, canvasKickPointModel: CanvasKickPointModel) {
        super();
        this._canvasModel = canvasModel;
        this._canvasKickPointModel = canvasKickPointModel;
        this._segment = segment;
        this.boreholeTop = hasBorehole(segment) ? segment.boreholeTop : segment.casingTop;
        this.boreHoleBottom = hasBorehole(segment) ? segment.boreholeBottom : segment.casingBottom;
        this.kickpoint = canvasKickPointModel.firstKickPointFt;
        this.startsBeforeKickPoint = this.boreholeTop < canvasKickPointModel.firstKickPointFt;
        this.endsAfterKickPoint = this.boreHoleBottom > canvasKickPointModel.firstKickPointFt;
        this.startsAfterKickPoint = this.boreholeTop > canvasKickPointModel.firstKickPointFt;
        this.topSegment = this.startsBeforeKickPoint;
        this.toFromKickPoint = this.startsBeforeKickPoint && this.endsAfterKickPoint;
        this.bottomSegment = this.endsAfterKickPoint;
        this.segmentIndex = segment.index + 1;
        this.hasShoe = segment.casingShoeDepth > 0;
    }

    /**
     * Main methoid to calculate drawing locations for all segments
     * @param canvasBoreHoleModel
     * @param canvasKickPointModel
     * @param topStringCount
     * @param bottomStringCount
     * @param boreHoleArrayLength
     * @param lastRightXY
     */
    setCanvasXY(canvasBoreHoleModel: CanvasSchematicBoreHoleModel, canvasKickPointModel: CanvasKickPointModel, topStringCount: number, bottomStringCount: number, boreHoleArrayLength: number) {
        let multiplier = boreHoleArrayLength - this.segmentIndex;
        let xOffset = (multiplier * SchematicConstants.schematicWellWidth);
        let verticalLength = (canvasKickPointModel.canvasXY.Y - SchematicConstants.schematicTop) / topStringCount;
        let diagonalLength = (SchematicConstants.schematicBottom - (canvasKickPointModel.canvasXY.Y - SchematicConstants.schematicTop + (2 * this.quadraticCurveHeight))) / bottomStringCount;
        let previousStringBottom = verticalLength * (this.segmentIndex - 1);
        let currentStringBottom = verticalLength * (this.segmentIndex);

        //way of figuring out how far down the canvas we have to start based on segment index
        let diagonalMulitplier = this.getDiagonalLengthMultiplier(boreHoleArrayLength, canvasBoreHoleModel.segmentIndex, bottomStringCount);

        if (canvasBoreHoleModel.segmentIndex != boreHoleArrayLength) {

            //BEFORE kick point
            if (canvasBoreHoleModel.topSegment) {
                let startX = canvasKickPointModel.canvasXY.X + xOffset;
                let startXLeft = canvasKickPointModel.canvasXY.X - xOffset;

                //VERTICAL segment
                if (canvasBoreHoleModel.toFromKickPoint) {
                    //bottom Y at kick point Y

                    //add VERTICAL
                    this.addVerticalCementStringXYs(startX, previousStringBottom, canvasKickPointModel.canvasXY.Y - SchematicConstants.schematicTop, true, false, canvasBoreHoleModel.toFromKickPoint);
                    this.addVerticalCementStringXYs(startXLeft, previousStringBottom, canvasKickPointModel.canvasXY.Y - SchematicConstants.schematicTop, false, false, canvasBoreHoleModel.toFromKickPoint);

                } else {
                    //bottom Y at calculated length

                    //add VERTICAL
                    this.addVerticalCementStringXYs(startX, previousStringBottom, currentStringBottom, true, this.hasShoe, canvasBoreHoleModel.toFromKickPoint);
                    this.addVerticalCementStringXYs(startXLeft, previousStringBottom, currentStringBottom, false, this.hasShoe, canvasBoreHoleModel.toFromKickPoint);
                }
            }

            //After kick point
            if (canvasBoreHoleModel.bottomSegment) {
                //normal startY, previous string bottom
                let startY = previousStringBottom + SchematicConstants.schematicTop;
                let startX = canvasKickPointModel.canvasXY.X + xOffset;
                let startXLeft = canvasKickPointModel.canvasXY.X - xOffset;

                //do we have the ONE string that starts BEFORE the kick point and ends after the kick point?
                if (canvasBoreHoleModel.topSegment) {
                    //use current string bottom for starting the quad curve (connected to the vertical!)
                    startY = canvasKickPointModel.canvasXY.Y + SchematicConstants.schematicTop;

                    //add QUADRATIC values for the curve
                    this.addQuadraticCementStringXYs(startX, startY, true);
                    this.addQuadraticCementStringXYs(startXLeft, startY, false);

                    //calc quad curve data
                    let quadraticRightCurveData = this.getQuadraticCurveData(startX, startY);
                    let quadraticLeftCurveData = this.getQuadraticCurveData(startXLeft, startY);

                    //add DIAGONAL
                    this.addDiagonalCementStringXYs(quadraticRightCurveData.endXY.X, quadraticRightCurveData.endXY.Y, diagonalLength, true);                    
                    this.addDiagonalCementStringXYs(quadraticLeftCurveData.endXY.X, quadraticLeftCurveData.endXY.Y, diagonalLength, false);

                } else {
                    //we have a string that starts/ends AFTER the kick point
                    
                    //add VERICAL CASING to borehole top
                    this.addVerticalCasingToKickPoint(startX, true);
                    this.addVerticalCasingToKickPoint(startXLeft, false);

                    //add QUADRATIC CASING for the curve
                    this.addQuadraticCasingStringXYs(startX, (this._canvasKickPointModel.canvasXY.Y + SchematicConstants.schematicTop), true);
                    this.addQuadraticCasingStringXYs(startXLeft, (this._canvasKickPointModel.canvasXY.Y + SchematicConstants.schematicTop), false);

                    //add DIAGONAL
                    this.addDiagonalPostKickPointCementStringXYs(startX, canvasKickPointModel.canvasXY.Y, diagonalLength, diagonalMulitplier, true);
                    this.addDiagonalPostKickPointCementStringXYs(startXLeft, canvasKickPointModel.canvasXY.Y, diagonalLength, diagonalMulitplier, false);
                }
            }
        } else {
            //FINAL (center) STRING - deal with differently!
            let startXRight = canvasKickPointModel.canvasXY.X + (2 * xOffset);
            let startXLeft = canvasKickPointModel.canvasXY.X + xOffset;

            //final string has kick point in it?
            if (canvasBoreHoleModel.topSegment) {

                //add vertical casing to kick point
                this.addVerticalCementStringXYs(startXRight, previousStringBottom, canvasKickPointModel.canvasXY.Y - SchematicConstants.schematicTop, true, this.hasShoe, canvasBoreHoleModel.toFromKickPoint, true);
                this.addVerticalCementStringXYs(startXLeft, previousStringBottom, canvasKickPointModel.canvasXY.Y - SchematicConstants.schematicTop, false, this.hasShoe, canvasBoreHoleModel.toFromKickPoint, true); 
            }
            if (canvasBoreHoleModel.bottomSegment) {

                //final string starts BEFORE kick point and ends AFTER kick point
                if (canvasBoreHoleModel.topSegment) {
                    let startY = canvasKickPointModel.canvasXY.Y;

                    //add QUADRATIC values for the curve
                    this.addQuadraticCasingStringXYs(startXRight, startY, true);
                    this.addQuadraticCasingStringXYs(startXLeft, startY, false);

                    //calc quad curve
                    let quadraticRightCurveData = this.getQuadraticCurveData(startXRight, startY);
                    let quadraticLeftCurveData = this.getQuadraticCurveData(startXLeft, startY);

                    //add DIAGONAL
                    let casingEndRightXY = this.addDiagonalCementStringXYs(quadraticRightCurveData.endXY.X, quadraticRightCurveData.endXY.Y, diagonalLength, true, true);
                    let casingEndLeftXY = this.addDiagonalCementStringXYs(quadraticLeftCurveData.endXY.X, quadraticLeftCurveData.endXY.Y, diagonalLength, false, true);

                    //add coords for closing of final string
                    this.addFinalStringCasingEndXYs(casingEndRightXY, casingEndLeftXY);

                    if (this.hasShoe) {
                        this.addFinalStringShoeXYs(casingEndRightXY, casingEndLeftXY);
                    }

                } else {
                    //add DIAGONAL 
                    let casingEndRightXY = this.addDiagonalPostKickPointCementStringXYs(startXRight, canvasKickPointModel.canvasXY.Y, diagonalLength, diagonalMulitplier, true, true);
                    let casingEndLeftXY = this.addDiagonalPostKickPointCementStringXYs(startXLeft, canvasKickPointModel.canvasXY.Y, diagonalLength, diagonalMulitplier, false, true);

                    //add coords for closing of final string
                    this.addFinalStringCasingEndXYs(casingEndRightXY, casingEndLeftXY);

                    if (this.hasShoe) {
                        this.addFinalStringShoeXYs(casingEndRightXY, casingEndLeftXY);
                    }
                }
            }
        }
    }

    addFinalStringShoeXYs(casingEndRightXY: CanvasXYOrientation, casingEndLeftXY: CanvasXYOrientation): void {
        this.finalStringShoeLocations.push(casingEndRightXY);
        this.finalStringShoeLocations.push(casingEndLeftXY);
    }

    addFinalStringCasingEndXYs(casingEndRightXY: CanvasXYOrientation, casingEndLeftXY: CanvasXYOrientation): void {
        this.finalStringCasingCloseLocations.push(casingEndRightXY);
        this.finalStringCasingCloseLocations.push(casingEndLeftXY);
    }

    /**
     * Adds double set of post kick point diagonal string x/y's (both 'start' and 'end' at the same time - post kick point but no curve!)
     * @param startX
     * @param startY
     * @param diagonalLength
     * @param diagonalMulitplier
     * @param right
     */
    addDiagonalPostKickPointCementStringXYs(startX: number, startY: number, diagonalLength: number, diagonalMulitplier: number, right: boolean, isFinalString: boolean = false): any {
        //offset for a single diagonal string
        let diagonalCoordsStandardOffset = this.getCoordsByLength(diagonalLength);
        //starting x/y for any string that starts after the kick point
        let diagonalCoordsMultiplied = this.getCoordsByLength(diagonalLength * diagonalMulitplier);

        //TRUE TOP
        let topX = startX + this.quadraticCurveWidth + diagonalCoordsMultiplied.X;
        let topY = startY + this.quadraticCurveHeight + SchematicConstants.schematicTop + diagonalCoordsMultiplied.Y;

        //OFFSET TOP
        let cementOffsetStartX = this.getCementBottomOffset(topX, right, true, false);
        let cementOffsetStartY = this.getCementBottomOffset(topY, right, false, false);

        //TRUE END
        let bottomX = startX + this.quadraticCurveWidth + diagonalCoordsMultiplied.X + diagonalCoordsStandardOffset.X;
        let bottomY = startY + this.quadraticCurveHeight + SchematicConstants.schematicTop + diagonalCoordsMultiplied.Y + diagonalCoordsStandardOffset.Y;

        //OFFSET END - because right is shorter then the left on all diagonals!
        let cementOffsetEndX = this.getCementBottomOffset(bottomX, right, true);
        let cementOffsetEndY = this.getCementBottomOffset(bottomY, right, false);

        //Add shoe?
        if (this.hasShoe && !isFinalString) {
            if (right) {
                this.addShoeStringXYs(CanvasWellStringDirections.diagonal, bottomX - (SchematicConstants.schematicWellWidth / 2), bottomY, right);
            } else {
                this.addShoeStringXYs(CanvasWellStringDirections.diagonal, bottomX + (SchematicConstants.schematicWellWidth / 2), bottomY, right);
            }
        }

        //Add label info boxes
        let labelStringXY = new CanvasSchematicStringLabel(this._canvasModel, cementOffsetEndX, cementOffsetEndY, CanvasWellStringDirections.diagonal, right, this._segment);

        //START CEMENT
        //starts at multiplier to get further down the canvas as we go up in index
        let diagonalStringXY = new CanvasXYOrientation(
            CanvasWellStringDirections.diagonal,
            cementOffsetStartX,
            cementOffsetStartY,
            null);

        //END CEMENT
        //ends with standard (1 segment) offset from mulitplied x/y
        let diagonalEndStringXY2 = new CanvasXYOrientation(
            CanvasWellStringDirections.diagonal,
            cementOffsetEndX,
            cementOffsetEndY,
            null);
        
        //START CASING
        let diagonalCasingStringXY = new CanvasXYOrientation(
            CanvasWellStringDirections.diagonal,
            this.getCasingXFromCement(topX, right),
            topY,
            null);

        //END CASING
        let diagonalCasingEndStringXY2 = new CanvasXYOrientation(
            CanvasWellStringDirections.diagonal,
            this.getCasingXFromCement(bottomX, right),
            bottomY,
            null);

        //add them to the array
        if (right) {
            if (!isFinalString) {
                //CEMENT
                this.fromToRightCement.push(diagonalStringXY);
                this.fromToRightCement.push(diagonalEndStringXY2);
            }
            //CASING
            this.fromToRightCasing.push(diagonalCasingStringXY);
            this.fromToRightCasing.push(diagonalCasingEndStringXY2);
            this.labelLocations.push(labelStringXY);
        } else {
            if (!isFinalString) {
                this.fromToLeftCement.push(diagonalStringXY);
                this.fromToLeftCement.push(diagonalEndStringXY2);
            }
            this.fromToLeftCasing.push(diagonalCasingStringXY);
            this.fromToLeftCasing.push(diagonalCasingEndStringXY2);
            this.labelLocations.push(labelStringXY);
        }

        //if last string, return casing end x/y for closing casing line
        if (isFinalString) {
            return diagonalCasingEndStringXY2;
        }

    }

    /**
     * Adds single set of cement x/y's (from end of curve to end of first post kick point string - post kick point)
     * @param startX
     * @param startY
     * @param diagonalLength
     * @param right
     */
    addDiagonalCementStringXYs(startX: number, startY: number, diagonalLength: number, right: boolean, isFinalString: boolean = false): any {

        //CEMENT
        // true length
        let diagonalCoords = this.getCoordsByLength(diagonalLength);
        let cementX = startX + diagonalCoords.X;
        let cementY = startY + diagonalCoords.Y;

        //OFFSET length because right is shorter then the left on all diagonals!
        let cementOffsetX = this.getCementBottomOffset(cementX, right, true);
        let cementOffsetY = this.getCementBottomOffset(cementY, right, false);
        let diagonalStringXY = new CanvasXYOrientation(CanvasWellStringDirections.diagonal, cementOffsetX, cementOffsetY, null);

        //CASING
        let casingX = this.getCasingXFromCement(cementX, right);
        let diagonalCasingStringXY = new CanvasXYOrientation(CanvasWellStringDirections.diagonal, casingX, cementY, null);

        
        //Add shoe?
        if (this.hasShoe && !isFinalString) {
            this.addShoeStringXYs(CanvasWellStringDirections.diagonal, casingX, cementY, right);
        }

        //Add label info boxes
        let labelStringXY = new CanvasSchematicStringLabel(this._canvasModel, cementOffsetX, cementOffsetY, CanvasWellStringDirections.diagonal, right, this._segment);

        if (right) {
            if (!isFinalString) {
                this.fromToRightCement.push(diagonalStringXY);
            }
            this.fromToRightCasing.push(diagonalCasingStringXY);
            this.labelLocations.push(labelStringXY);
        } else {
            if (!isFinalString) {
                this.fromToLeftCement.push(diagonalStringXY);
            }
            this.fromToLeftCasing.push(diagonalCasingStringXY);
            this.labelLocations.push(labelStringXY);
        }

        if (isFinalString) {
            return diagonalCasingStringXY;
        }
    }

    /**
     * Adds single set of cement x/y's (from vertical end to end of curve - at kick point)
     * @param startX
     * @param startY
     * @param right
     */
    addQuadraticCementStringXYs(startX: number, startY: number, right:boolean): void {
        
        //CEMENT
        let quadraticCurveData = this.getQuadraticCurveData(startX, startY);
        let quadStringXY = new CanvasXYOrientation(CanvasWellStringDirections.curve, null, null, quadraticCurveData);

        //CASING
        this.addQuadraticCasingStringXYs(startX, startY, right);

        /*
        this._canvasModel.ctx.moveTo(0, quadraticCurveData.endXY.Y)
        this._canvasModel.ctx.lineTo(this._canvasModel.canvasWidth, quadraticCurveData.endXY.Y);
        this._canvasModel.ctx.lineWidth = 1;
        this._canvasModel.ctx.strokeStyle = "#00ffff";
        this._canvasModel.ctx.stroke();
        */


        if (right) {
            this.fromToRightCement.push(quadStringXY);
        } else {
            this.fromToLeftCement.push(quadStringXY);
        }
        
    }

    /**
     * Adds double set of cement x/y's (from vertical start to vertical bottom - pre kick point)
     * @param X
     * @param previousStringBottomY
     * @param currentStringBottomY
     * @param right
     */
    addVerticalCementStringXYs(X: number, previousStringBottomY: number, currentStringBottomY: number, right: boolean, hasShoe: boolean, toKickPoint: boolean, isFinalString:boolean = false): void {
        let verticalTopStringXY = new CanvasXYOrientation(CanvasWellStringDirections.vertical, X, SchematicConstants.schematicTop + previousStringBottomY);
        let verticalBottomStringXY = new CanvasXYOrientation(CanvasWellStringDirections.vertical, X, SchematicConstants.schematicTop + currentStringBottomY);

        
        //Add shoe?
        if (hasShoe && !isFinalString) {
            this.addShoeStringXYs(CanvasWellStringDirections.vertical, verticalBottomStringXY.X - (SchematicConstants.schematicWellWidth/2), verticalBottomStringXY.Y, right);
        }

        //Add label info boxes
        let labelStringXY = new CanvasSchematicStringLabel(this._canvasModel, verticalBottomStringXY.X, verticalBottomStringXY.Y, CanvasWellStringDirections.vertical, right, this._segment);

        //Add CEMENT & CASING
        if (right) {
            if (!isFinalString) {
                //CEMENT
                this.fromToRightCement.push(verticalTopStringXY);
                this.fromToRightCement.push(verticalBottomStringXY);
            }
            //CASING
            this.fromToRightCasing.push(this.getVerticalCasingRightFromCementRight(verticalTopStringXY, true, false));
            this.fromToRightCasing.push(this.getVerticalCasingRightFromCementRight(verticalBottomStringXY, false, false));
            //LABEL
            if (!toKickPoint) {
                this.labelLocations.push(labelStringXY);
            }
            
        } else {
            if (!isFinalString) {
                //CEMENT
                this.fromToLeftCement.push(verticalTopStringXY);
                this.fromToLeftCement.push(verticalBottomStringXY);
            }
            //CASING
            this.fromToLeftCasing.push(this.getVerticalCasingRightFromCementRight(verticalTopStringXY, true, true));
            this.fromToLeftCasing.push(this.getVerticalCasingRightFromCementRight(verticalBottomStringXY, false, true));
            //LABEL
            if (!toKickPoint) {
                this.labelLocations.push(labelStringXY);
            }
        }
        
    }

    /**
     * Adds casing in a curve
     * @param startX - CEMENT X not casing X!
     * @param startY
     * @param right
     */
    addQuadraticCasingStringXYs(startX: number, startY: number, right: boolean): void {
        //CASING
        let startXCasing = this.getCasingXFromCement(startX, right);
        let quadraticCasingCurveData = this.getQuadraticCurveData(startXCasing, startY);
        let quadCasingStringXY = new CanvasXYOrientation(CanvasWellStringDirections.curve, null, null, quadraticCasingCurveData);

        if (right) {
            this.fromToRightCasing.push(quadCasingStringXY);
        } else {
            this.fromToLeftCasing.push(quadCasingStringXY);
        }
    }

    addVerticalCasingToKickPoint(X: number, right: boolean): void {
        let casingX = this.getCasingXFromCement(X, right);
        let verticalTopStringXY = new CanvasXYOrientation(CanvasWellStringDirections.vertical, casingX, SchematicConstants.schematicTop);
        let verticalBottomStringXY = new CanvasXYOrientation(CanvasWellStringDirections.vertical, casingX, this._canvasKickPointModel.canvasXY.Y);
        if (right) {
            this.fromToRightCasing.push(verticalTopStringXY);
            this.fromToRightCasing.push(verticalBottomStringXY);
        } else {
            this.fromToLeftCasing.push(verticalTopStringXY);
            this.fromToLeftCasing.push(verticalBottomStringXY);
        }
       
    }

    /**
     * 
     * @param type
     * @param casingX
     * @param casingY
     * @param right
     */
    addShoeStringXYs(type: CanvasWellStringDirections, casingX: number, casingY: number, right:boolean ) {
        let newCanvasXYShoe = new CanvasXYShoe();
        newCanvasXYShoe.X = casingX;
        newCanvasXYShoe.Y = casingY;
        newCanvasXYShoe.type = type;
        newCanvasXYShoe.right = right;
        this.shoeLocations.push(newCanvasXYShoe);
    }

    drawCasing(canvasBoreHoleModel: CanvasSchematicBoreHoleModel): void {
        this._canvasModel.ctx.beginPath();
        this._canvasModel.ctx.strokeStyle = this.strokeStyleCasing;
        this._canvasModel.ctx.lineWidth = this.lineWidthCasing;
        //RIGHT of center
        canvasBoreHoleModel.fromToRightCasing.forEach((canvasXYOrientation: CanvasXYOrientation, index) => {
            //draw VERTICAL
            if (canvasXYOrientation.orientation == CanvasWellStringDirections.vertical) {
                if (index == 0) {
                    this._canvasModel.ctx.moveTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                } else {
                    this._canvasModel.ctx.lineTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                }
            }
            //draw CURVE
            if (canvasXYOrientation.orientation == CanvasWellStringDirections.curve) {
                this._canvasModel.ctx.quadraticCurveTo(
                    canvasXYOrientation.quadraticXYs.quadXY.X,
                    canvasXYOrientation.quadraticXYs.quadXY.Y,
                    canvasXYOrientation.quadraticXYs.endXY.X,
                    canvasXYOrientation.quadraticXYs.endXY.Y);
            }
            //draw DIAGONAL
            if (canvasXYOrientation.orientation == CanvasWellStringDirections.diagonal) {
                if (index == 0) {
                    this._canvasModel.ctx.moveTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                } else {
                    this._canvasModel.ctx.lineTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                }

            }
        });
        this._canvasModel.ctx.stroke();


        this._canvasModel.ctx.beginPath();
        //LEFT of center
        canvasBoreHoleModel.fromToLeftCasing.forEach((canvasXYOrientation: CanvasXYOrientation, index) => {
            //draw VERTICAL
            if (canvasXYOrientation.orientation == CanvasWellStringDirections.vertical) {
                if (index == 0) {
                    this._canvasModel.ctx.moveTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                } else {
                    this._canvasModel.ctx.lineTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                }
            }
            //draw CURVE
            if (canvasXYOrientation.orientation == CanvasWellStringDirections.curve) {
                this._canvasModel.ctx.quadraticCurveTo(
                    canvasXYOrientation.quadraticXYs.quadXY.X,
                    canvasXYOrientation.quadraticXYs.quadXY.Y,
                    canvasXYOrientation.quadraticXYs.endXY.X,
                    canvasXYOrientation.quadraticXYs.endXY.Y);
            }
            //draw DIAGONAL
            if (canvasXYOrientation.orientation == CanvasWellStringDirections.diagonal) {
                if (index == 0) {
                    this._canvasModel.ctx.moveTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                } else {
                    this._canvasModel.ctx.lineTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                }

            }
        });
        this._canvasModel.ctx.stroke();

    }

    /**
     * Main method for drawing cement lines on the canvas
     * @param canvasBoreHoleModel
     */
    drawCement(canvasBoreHoleModel: CanvasSchematicBoreHoleModel): void {

        this._canvasModel.ctx.beginPath();
        this._canvasModel.ctx.strokeStyle = this.strokeStyleCement;
        this._canvasModel.ctx.lineWidth = this.lineWidthCement;

        //RIGHT of center
        canvasBoreHoleModel.fromToRightCement.forEach((canvasXYOrientation: CanvasXYOrientation, index) => {
            //draw VERTICAL
            if (canvasXYOrientation.orientation == CanvasWellStringDirections.vertical) {
                if (index == 0) {
                    this._canvasModel.ctx.moveTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                } else {
                    this._canvasModel.ctx.lineTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                }
            }
            //draw CURVE
            if (canvasXYOrientation.orientation == CanvasWellStringDirections.curve) {
                this._canvasModel.ctx.quadraticCurveTo(
                    canvasXYOrientation.quadraticXYs.quadXY.X,
                    canvasXYOrientation.quadraticXYs.quadXY.Y,
                    canvasXYOrientation.quadraticXYs.endXY.X,
                    canvasXYOrientation.quadraticXYs.endXY.Y);
            }
            //draw DIAGONAL
            if (canvasXYOrientation.orientation == CanvasWellStringDirections.diagonal) {
                if (index == 0) {
                    this._canvasModel.ctx.moveTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                } else {
                    this._canvasModel.ctx.lineTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                }
                
            }
        });
        this._canvasModel.ctx.stroke();
        
        this._canvasModel.ctx.beginPath();

        //LEFT of center
        canvasBoreHoleModel.fromToLeftCement.forEach((canvasXYOrientation: CanvasXYOrientation, index) => {
            //draw VERTICAL
            if (canvasXYOrientation.orientation == CanvasWellStringDirections.vertical) {
                if (index == 0) {
                    this._canvasModel.ctx.moveTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                } else {
                    this._canvasModel.ctx.lineTo(canvasXYOrientation.X, canvasXYOrientation.Y);
                }
            }
            //draw CURVE
            if (canvasXYOrientation.orientation == CanvasWellStringDirections.curve) {
                this._canvasModel.ctx.quadraticCurveTo(
                    canvasXYOrientation.quadraticXYs.quadXY.X,
                    canvasXYOrientation.quadraticXYs.quadXY.Y,
                    canvasXYOrientation.quadraticXYs.endXY.X,
                    canvasXYOrientation.quadraticXYs.endXY.Y);
            }
            //draw DIAGONAL
            if (canvasXYOrientation.orientation == CanvasWellStringDirections.diagonal) {
                this._canvasModel.ctx.lineTo(canvasXYOrientation.X, canvasXYOrientation.Y);
            }

        });

        this._canvasModel.ctx.stroke();
    }

    drawShoes(canvasBoreHoleModel: CanvasSchematicBoreHoleModel): void {
        for (let shoe of canvasBoreHoleModel.shoeLocations) {
            
            if (shoe.type == CanvasWellStringDirections.vertical) {

                this._canvasModel.ctx.beginPath();
                this._canvasModel.ctx.fillStyle = "#000000";

                this._canvasModel.ctx.moveTo(shoe.X, shoe.Y);

                //RIGHT
                if (shoe.right) {
                    this._canvasModel.ctx.lineTo(shoe.X, shoe.Y - this.shoeHW);
                    this._canvasModel.ctx.lineTo(shoe.X + this.shoeHW, shoe.Y);
                } else {
                    //LEFT
                    this._canvasModel.ctx.lineTo(shoe.X + this.shoeHW, shoe.Y);
                    this._canvasModel.ctx.lineTo(shoe.X + this.shoeHW, shoe.Y - this.shoeHW);
                }


                this._canvasModel.ctx.closePath();
                this._canvasModel.ctx.fill();
            }

            if (shoe.type == CanvasWellStringDirections.diagonal) {
                this.drawDiagonalShoe(shoe.X, shoe.Y, shoe.right);
            }
            

            
        }
    }
    drawDiagonalShoe(shoeX: number, shoeY: number, right: boolean, isFinalString: boolean = false): void {

        this._canvasModel.ctx.beginPath();
        this._canvasModel.ctx.fillStyle = "#000000";

        if (isFinalString) {
            let paddedShoeY = shoeY + 1; //Aligns with the casing bottom line!
            //mods for final string shoes
            this._canvasModel.ctx.moveTo(shoeX, paddedShoeY);
            if (right) {
                this._canvasModel.ctx.lineTo(shoeX - 10, paddedShoeY - 20);
                this._canvasModel.ctx.lineTo(shoeX + 16, paddedShoeY);
            } else {
                this._canvasModel.ctx.lineTo(shoeX - 11, paddedShoeY - 20);
                this._canvasModel.ctx.lineTo(shoeX - 16, paddedShoeY);
            }
        } else {
            //all other diagonal shoes
            this._canvasModel.ctx.moveTo(shoeX, shoeY);
            if (right) {
                this._canvasModel.ctx.lineTo(shoeX - 10, shoeY - 20);
                this._canvasModel.ctx.lineTo(shoeX + 16, shoeY - 8);
            } else {
                this._canvasModel.ctx.lineTo(shoeX - 11, shoeY - 20);
                this._canvasModel.ctx.lineTo(shoeX - 16, shoeY + 8);
            }
        }
        
        this._canvasModel.ctx.closePath();
        this._canvasModel.ctx.fill();
    }

    drawFinalCasingClose(canvasBoreHoleModel: CanvasSchematicBoreHoleModel): void {
        if (canvasBoreHoleModel.finalStringCasingCloseLocations.length > 0) {
            let startXY = canvasBoreHoleModel.finalStringCasingCloseLocations[0];
            let endXY = canvasBoreHoleModel.finalStringCasingCloseLocations[1];
            this._canvasModel.ctx.beginPath();
            this._canvasModel.ctx.lineWidth = this.lineWidthCasing;
            this._canvasModel.ctx.strokeStyle = this.strokeStyleCasing;
            this._canvasModel.ctx.moveTo(startXY.X, startXY.Y);
            this._canvasModel.ctx.lineTo(endXY.X, endXY.Y);
            this._canvasModel.ctx.stroke();
        }
    }

    drawFinalShoes(canvasBoreHoleModel: CanvasSchematicBoreHoleModel): void {
        if (canvasBoreHoleModel.finalStringShoeLocations.length > 0) {
            let rightXY = canvasBoreHoleModel.finalStringCasingCloseLocations[1];
            let leftXY = canvasBoreHoleModel.finalStringCasingCloseLocations[0];
            this.drawDiagonalShoe(rightXY.X, rightXY.Y, true, true);
            this.drawDiagonalShoe(leftXY.X, leftXY.Y, false, true);
        }  

    }

    drawLabels(canvasBoreHoleModel: CanvasSchematicBoreHoleModel, index: number, precedingLabel: CanvasSchematicBoreHoleModel): void {
        //odd/even switch
        let preferRight = (Math.abs(index % 2) == 0);
        //loop every 2        

        for (let i = 0; i < canvasBoreHoleModel.labelLocations.length; i += 2) {
            //load both a left & right label for the given string
            let rightLabel = canvasBoreHoleModel.labelLocations[i];
            let precedingRight: CanvasSchematicStringLabel;
            let precedingLeft: CanvasSchematicStringLabel;
            let leftLabel = canvasBoreHoleModel.labelLocations[i + 1];
            let drawRight = preferRight && rightLabel.isLeaderBoxRightViable;
            let previousDrawRight: boolean = true;
            let intersectsPrecedingRight: boolean = false;
            let intersectsPrecedingLeft: boolean = false;
            if (precedingLabel != null) {
                precedingRight = precedingLabel.labelLocations[i];
                precedingLeft = precedingLabel.labelLocations[i + 1];
                let bottomYLeftMax = Math.max(...precedingLeft.labelBoxXYs.map(xy => xy.Y));
                let bottomYPreviousLeftBox = precedingLeft.labelBoxXYs.find(xy => xy.Y == bottomYLeftMax);
                let bottomYRightMax = Math.max(...precedingRight.labelBoxXYs.map(xy => xy.Y));
                let bottomYPreviousRightBox = precedingRight.labelBoxXYs.find(xy => xy.Y == bottomYRightMax);
                let topYLeftMin = Math.min(...leftLabel.labelBoxXYs.map(xy => xy.Y));
                let topYCurrentLeftBox = leftLabel.labelBoxXYs.find(xy => xy.Y == topYLeftMin);
                let topYRightMin = Math.min(...rightLabel.labelBoxXYs.map(xy => xy.Y));
                let topYCurrentRightBox = rightLabel.labelBoxXYs.find(xy => xy.Y == topYRightMin);
                intersectsPrecedingLeft = bottomYPreviousLeftBox.Y > topYCurrentLeftBox.Y;
                intersectsPrecedingRight = bottomYPreviousRightBox.Y > topYCurrentRightBox.Y;
                previousDrawRight = !preferRight && precedingRight.isLeaderBoxRightViable;
            }           
            
            //if we want to draw on the right and have room on the right
            if (drawRight) {
                //draw right
                rightLabel.draw(intersectsPrecedingRight && previousDrawRight);
            } else {
                //draw left
                leftLabel.draw(intersectsPrecedingLeft && !previousDrawRight);
            }
        }
    }

    /**
     * Calculates offsets for right and left strings
     *  right strings are SHORTER then the left on ALL diagonals
     * @param value
     * @param right
     * @param x
     * @param bottom
     */
    getCementBottomOffset(value: number, right: boolean, x: boolean, bottom:boolean = true): number {

        const offsetRightLeft = this.getCoordsByLength(5);
        //right of left coordinate offset?
        if (right) {
            //x or y?
            if (x) {
                //bottom or top?
                if (bottom) {
                    return value - offsetRightLeft.X;
                } else {
                    return value + offsetRightLeft.X;
                }
            } else {
                if (bottom) {
                    return value - offsetRightLeft.Y;
                } else {
                    return value + offsetRightLeft.Y;
                }
            }
        } else {
            if (x) {
                if (bottom) {
                    return value + offsetRightLeft.X;
                } else {
                    return value - offsetRightLeft.X;
                }
            } else {
                if (bottom) {
                    return value + offsetRightLeft.Y;
                } else {
                    return value - offsetRightLeft.Y;
                }
                
            }
        }
    }

    getVerticalCasingRightFromCementRight(cementXY: CanvasXYOrientation, top: boolean, left: boolean): CanvasXYOrientation {
        let x = this.getCasingXFromCement(cementXY.X, true);
        if (left) x = this.getCasingXFromCement(cementXY.X, false);
        let y = cementXY.Y
        if (top) y = SchematicConstants.schematicTop;
        return new CanvasXYOrientation(cementXY.orientation, x, y); 
    }

    getCasingXFromCement(x: number, right: boolean): number {
        if (right) {
            return x - (SchematicConstants.schematicWellWidth / 2);
        } else {
            return x + (SchematicConstants.schematicWellWidth / 2);
        }
    }
    /**
     * Figure out how far down the canvas a post kickpoint diagonal segment has to start
     * @param boreHoleArrayLength
     * @param segmentIndex
     * @param bottomStringCount
     */
    getDiagonalLengthMultiplier(boreHoleArrayLength: number, segmentIndex: number, bottomStringCount: number): number {
        return Math.abs(boreHoleArrayLength - segmentIndex - (bottomStringCount - 1));
    }

    /**
     * 
     * @param length - length of post kick point string in true feet
     */
    getCoordsByLength(length: number): CanvasXY {
        let returnXY = new CanvasXY();
        // .5 works for the quad angle created (is in alignment with it)
        returnXY.X = (Math.sin(.5) * length);
        returnXY.Y = (Math.cos(.5) * length);

        return returnXY;
    }

    /**
     * Standard method creating a curve
     * @param startX
     * @param startY
     */
    getQuadraticCurveData(startX: number, startY: number): CanvasQuadraticXY {

        let returnCanvasQuadraticXY = new CanvasQuadraticXY();

        returnCanvasQuadraticXY.startXY.X = startX;
        returnCanvasQuadraticXY.startXY.Y = startY;
        returnCanvasQuadraticXY.endXY.X = startX + this.quadraticCurveWidth;
        returnCanvasQuadraticXY.endXY.Y = startY + this.quadraticCurveHeight;
        returnCanvasQuadraticXY.quadXY.X = startX;
        returnCanvasQuadraticXY.quadXY.Y = startY + this.quadraticCurveQuadY;

        return returnCanvasQuadraticXY;

    }
}


/**
 * modelf or drawing the application number
 * */
export class CanvasWellDataModel extends CanvasModel {
    wellName: string;
    wellNumber: string;
    applicationNumber: string;
    wellDirection: CasinPlanDiagramDirectionTypes;
    locationXY: CanvasXY = new CanvasXY();
    location: CanvasPolyXYLocations;
    paddingRL: number = 20
    paddingTB: number = 5
    setLocation(location: CanvasPolyXYLocations): void {
        this.location = location;
        this.calcLocationXY();
    }
    calcLocationXY(): void {
        switch (this.location) {
            case CanvasPolyXYLocations.bottomLeft:
                this.locationXY.X = this.paddingRL;
                this.locationXY.Y = this.canvasHeight - this.paddingTB;
                break;
            case CanvasPolyXYLocations.bottomRight:
                this.locationXY.X = this.canvasWidth - this.paddingRL;
                this.locationXY.Y = this.canvasHeight - this.paddingTB;
                break;
            case CanvasPolyXYLocations.topLeft:
                this.locationXY.X = this.paddingRL;
                this.locationXY.Y = this.paddingTB;
                break;
            case CanvasPolyXYLocations.topRight:
                this.locationXY.X = this.canvasWidth - this.paddingRL;
                this.locationXY.Y = this.paddingTB;
                break;
        }
    }
    draw(): void {
        this.ctx.beginPath();
        this.ctx.textAlign = "right";
        this.ctx.fillText("Application Number: " + this.applicationNumber, this.locationXY.X, this.locationXY.Y);
        this.ctx.textAlign = "left";

    }
}

/**
 * calculates the average for vertical/non-vertical strings based on available space and number of segments
 * */
export class SchematicCanvasWellStringLengthModel {
    countVertical: number;
    countNonVerical: number;
    verticalLength: number;
    nonVerticalLength: number;
    setAverageLengths(wellSegments: Array<WellSegmentModel>, canvasKickPointModel: CanvasKickPointModel): void {
        this.countVertical = wellSegments.filter((segment: WellSegmentModel) => segment.boreholeTop < canvasKickPointModel.firstKickPointFt || segment.boreholeBottom < canvasKickPointModel.firstKickPointFt).length;
        this.countNonVerical = wellSegments.filter((segment: WellSegmentModel) => segment.boreholeBottom > canvasKickPointModel.firstKickPointFt || segment.boreholeTop > canvasKickPointModel.firstKickPointFt).length;
        this.verticalLength = (canvasKickPointModel.canvasXY.Y - SchematicConstants.schematicTop) / this.countVertical;
        this.nonVerticalLength = (SchematicConstants.schematicBottom - canvasKickPointModel.canvasXY.Y) / this.countNonVerical;        
    }
    getCumlativeVerticalLengthBySegmentIndex(segmentIndex: number): number {
        return this.verticalLength * segmentIndex;
    }
    getCumlativeDiagonalLengthBySegmentIndex(segmentIndex: number): number {
        return this.nonVerticalLength * segmentIndex;
    }
}

/**
 * simple x/y
 * */
export class CanvasXY {
    X: number;
    Y: number;
}

/**
 * adds support for x/y with an orientation (diagonal, vertical, etc)
 * */
export class CanvasXYOrientation extends CanvasXY{
    orientation: CanvasWellStringDirections;
    quadraticXYs?: CanvasQuadraticXY;
    constructor(orientation: CanvasWellStringDirections, x?: number, y?: number, quadXYs?: CanvasQuadraticXY) {
        super();
        this.orientation = orientation;
        this.X = x;
        this.Y = y;
        this.quadraticXYs = quadXYs;
    }
}

/**
 * simple polygon model
 * */
export class CanvasPolygonXY extends CanvasXY {
    location: CanvasPolyXYLocations;
}

/**
 * single model for all values of a quadratic curve
 * */
export class CanvasQuadraticXY {
    startXY: CanvasXY = new CanvasXY();
    quadXY: CanvasXY = new CanvasXY();
    endXY: CanvasXY = new CanvasXY();
}

/**
 * simple shoe model
 * */
export class CanvasXYShoe extends CanvasXY {
    right: boolean;
    type: CanvasWellStringDirections;
}

/**
 * simple label model
 * */
export class CanvasXYLabel extends CanvasXY{
    right: boolean;
}

/**
 * draws missing data if no kickpoint
 * */
export class CanvasMissingData extends CanvasXY {
    text: string;
    fillStyle: string = "#b63e2e"; /*alert*/
    font: string = "18px Work Sans";
    _canvasModel: CanvasModel;

    constructor(canvasModel: CanvasModel, text: string) {
        super();
        this._canvasModel = canvasModel;
        this.X = canvasModel.canvasWidth / 2;
        this.Y = canvasModel.canvasHeight / 2;
        this.text = text;
    }

    draw(): void {
        this._canvasModel.ctx.beginPath();
        this._canvasModel.ctx.font = this.font;
        this._canvasModel.ctx.fillStyle = this.fillStyle;
        this._canvasModel.ctx.textAlign = "center";
        this._canvasModel.ctx.fillText(this.text, this.X, this.Y);
        this._canvasModel.ctx.closePath();
    }
}

/**
 * draws schematic label with number, circle, box and leader
 * */
export class CanvasSchematicStringLabel {
    segment: WellSegmentModel;
    canvasModel: CanvasModel;
    labelType: CanvasWellStringDirections;
    leaderXYs: CanvasXY[] = [];
    labelBoxXYs: CanvasXY[] = [];
    offsetLabelBoxXYs: CanvasXY[] = [];
    segmentNumberXY: CanvasXY = new CanvasXY();
    numberCircleXY: CanvasXY = new CanvasXY();
    casingSizeXY: CanvasXY = new CanvasXY();
    casingDepthXY: CanvasXY = new CanvasXY();
    casingTypeXY: CanvasXY = new CanvasXY();
    boreholeDepthXY: CanvasXY = new CanvasXY();
    boreholeSizeXY: CanvasXY = new CanvasXY();
    cementTopXY: CanvasXY = new CanvasXY();
    cementVolumeXY: CanvasXY = new CanvasXY();
    casingSizeFont: string = "bold 14px 'Work Sans'";
    casingTypeFont: string = "12px 'Work Sans'";
    circleRadius: number = 0;
    segmentIndex: number;
    verticalLeaderOffsetY: number = 3;
    verticalLeaderOffsetX: number = 5;
    verticalLeaderLength: number = 10;
    labelBoxWidth: number = 225;

    get labelBoxHeight(): number {
        if (this.segment == null) return 45;
        let height: number = 105;
        let dataPoints = 0;
        if (this.segment.casingType != null) dataPoints += 1;
        if (this.segment.boreholeDepth != null && !isNaN(parseFloat(this.segment.boreholeDepth.toString()))) dataPoints += 1;
        if (this.segment.boreholeDiameter != null && !isNaN(parseFloat(this.segment.boreholeDiameter.toString()))) dataPoints += 1;
        if (this.segment.casingDiameter != null && !isNaN(parseFloat(this.segment.casingDiameter.toString()))) dataPoints += 1;
        if (this.segment.casingLength != null && !isNaN(parseFloat(this.segment.casingLength.toString()))) dataPoints += 1;
        if (this.segment.cementTop != null && !isNaN(parseFloat(this.segment.cementTop.toString()))) dataPoints += 1;
        if (this.segment.cementVolume != null && !isNaN(parseFloat(this.segment.cementVolume.toString()))) dataPoints += 1;
        if (dataPoints > 0 && dataPoints < 7) dataPoints += 1;
        height = (dataPoints > 0) ? dataPoints * 15 : height;
        return height;
    }


    leaderStrokeStyle: string = "#000000";
    leaderLineWidth: number = 1;
    numberFont: string = "bold 18px 'Work Sans'";
    numberFontVerticalOffset: number = 6;           //offset segment number to be in the middle of the circle
    right: boolean;
    isLeaderBoxRightViable: boolean;
    constructor(canvasModel: CanvasModel, leaderX: number, leaderY: number, labelType: CanvasWellStringDirections, right: boolean, segment: WellSegmentModel) {
        this.canvasModel = canvasModel;
        this.segment = segment;
        this.labelType = labelType;
        this.right = right;
        this.circleRadius = this.setCircleRadius();
        this.setDrawingXYs(leaderX, leaderY, right);
    }

    setDrawingXYs(leaderX: number, leaderY: number, right: boolean): void {
        //calc the farthest RIGHT a box can start
        let maxRightLeaderX = SchematicConstants.schematicRight - this.labelBoxWidth - this.verticalLeaderOffsetX;
        //set if can be drawn on right
        this.setLeaderBoxRightViable(leaderX, maxRightLeaderX);
        //set leader XY's and return leader at box edge
        let horizontalLeaderXY = this.setLeaderXY(leaderX, leaderY, right);
        //set box locations
        this.setLabelBoxXYs(horizontalLeaderXY, right);
        //set segment number location
        this.setSegmentNumberXY(horizontalLeaderXY, right);
        //set segment number circle location 
        this.setNumberCircleXY(horizontalLeaderXY, right);
        //set casing SIZE location
        let factor: number = 1;
        if (hasBorehole(this.segment)) {
            if (this.segment.boreholeDiameter != null && !isNaN(parseFloat(this.segment.boreholeDiameter.toString()))) {
                this.setBoreholeSizeXY(horizontalLeaderXY, right, factor);
                factor += 1;
            }
            if (this.segment.boreholeDepth != null && !isNaN(parseFloat(this.segment.boreholeDepth.toString()))) {
                this.setBoreholeDepthXY(horizontalLeaderXY, right, factor);
                factor += 1;
            }           
        }
        if (hasCasing(this.segment)) {
            if (this.segment.casingDiameter != null && !isNaN(parseFloat(this.segment.casingDiameter.toString()))) {
                this.setCasingSizeXY(horizontalLeaderXY, right, factor);
                factor += 1;
            }
            if (this.segment.casingLength != null && !isNaN(parseFloat(this.segment.casingLength.toString()))) {
                this.setCasingDepthXY(horizontalLeaderXY, right, factor);
                factor += 1;
            }            
        }
        if (hasCement(this.segment)) {
            if (this.segment.cementTop != null && !isNaN(parseFloat(this.segment.cementTop.toString()))) {
                this.setCementTopXY(horizontalLeaderXY, right, factor);
                factor += 1;
            }
            if (this.segment.cementVolume != null && !isNaN(parseFloat(this.segment.cementVolume.toString()))) {
                this.setCementVolumeXY(horizontalLeaderXY, right, factor);
                factor += 1;
            }            
        }       
        //set casing TYPE location
        this.setCasingTypeLocation(horizontalLeaderXY, right, factor);
    }
    setLeaderBoxRightViable(leaderX: number, boxX: number): void {
        this.isLeaderBoxRightViable = boxX > (leaderX + (2 * this.verticalLeaderOffsetX));
    }
    setCasingTypeLocation(lastLeaderXY: CanvasXY, right: boolean, factor): void {
        if (right) {
            this.casingTypeXY.X = lastLeaderXY.X + this.getCasingOffset(true);
            this.casingTypeXY.Y = (lastLeaderXY.Y) + (factor * 2.25 * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        } else {
            this.casingTypeXY.X = lastLeaderXY.X - this.labelBoxWidth + this.getCasingOffset(true);
            this.casingTypeXY.Y = (lastLeaderXY.Y) + (factor * 2.25 * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        }
    }

    setCasingSizeXY(lastLeaderXY: CanvasXY, right: boolean, factor: number): void {
        if (right) {
            this.casingSizeXY.X = lastLeaderXY.X + this.getCasingOffset(true);
            this.casingSizeXY.Y = (lastLeaderXY.Y) + (factor * 2.25 * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        } else {
            this.casingSizeXY.X = lastLeaderXY.X - this.labelBoxWidth + this.getCasingOffset(true);
            this.casingSizeXY.Y = (lastLeaderXY.Y) + (factor * 2.25 * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        }
    }

    setCasingDepthXY(lastLeaderXY: CanvasXY, right: boolean, factor: number): void {
        if (right) {
            this.casingDepthXY.X = lastLeaderXY.X + this.getCasingOffset(true);
            this.casingDepthXY.Y = (lastLeaderXY.Y) + (2.25 * factor * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        } else {
            this.casingDepthXY.X = lastLeaderXY.X - this.labelBoxWidth + this.getCasingOffset(true);
            this.casingDepthXY.Y = (lastLeaderXY.Y) + (2.25 * factor * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        }
    }

    setBoreholeSizeXY(lastLeaderXY: CanvasXY, right: boolean, factor: number): void {
        if (right) {
            this.boreholeSizeXY.X = lastLeaderXY.X + this.getCasingOffset(true);
            this.boreholeSizeXY.Y = (lastLeaderXY.Y) + (factor * 2.25 * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        } else {
            this.boreholeSizeXY.X = lastLeaderXY.X - this.labelBoxWidth + this.getCasingOffset(true);
            this.boreholeSizeXY.Y = (lastLeaderXY.Y) + (factor * 2.25 * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        }
    }

    setBoreholeDepthXY(lastLeaderXY: CanvasXY, right: boolean, factor: number): void {
        if (right) {
            this.boreholeDepthXY.X = lastLeaderXY.X + this.getCasingOffset(true);
            this.boreholeDepthXY.Y = (lastLeaderXY.Y) + (factor * 2.25 * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        } else {
            this.boreholeDepthXY.X = lastLeaderXY.X - this.labelBoxWidth + this.getCasingOffset(true);
            this.boreholeDepthXY.Y = (lastLeaderXY.Y) + (factor * 2.25 * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        }
    }

    setCementTopXY(lastLeaderXY: CanvasXY, right: boolean, factor: number): void {
        if (right) {
            this.cementTopXY.X = lastLeaderXY.X + this.getCasingOffset(true);
            this.cementTopXY.Y = (lastLeaderXY.Y) + (factor * 2.25 * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        } else {
            this.cementTopXY.X = lastLeaderXY.X - this.labelBoxWidth + this.getCasingOffset(true);
            this.cementTopXY.Y = (lastLeaderXY.Y) + (factor * 2.25 * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        }
    }

    setCementVolumeXY(lastLeaderXY: CanvasXY, right: boolean, factor: number): void {
        if (right) {
            this.cementVolumeXY.X = lastLeaderXY.X + this.getCasingOffset(true);
            this.cementVolumeXY.Y = (lastLeaderXY.Y) + (factor * 2.25 * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        } else {
            this.cementVolumeXY.X = lastLeaderXY.X - this.labelBoxWidth + this.getCasingOffset(true);
            this.cementVolumeXY.Y = (lastLeaderXY.Y) + (factor * 2.25 * this.getCasingOffset(false)) - (this.labelBoxHeight / 2);
        }
    }


    getCasingOffset(x: boolean): number {
        if (x) {
            return (2.75 * this.circleRadius);
        } else {
            return (this.circleRadius / 2.75);
        }
    }

    drawCasingType(usingOffset: boolean = false): void {
        if (usingOffset) {
            this.casingTypeXY.Y += this.labelBoxHeight + 15;
        }
        this.canvasModel.ctx.beginPath();
        this.canvasModel.ctx.font = this.casingTypeFont;
        this.canvasModel.ctx.textAlign = "left";
        this.canvasModel.ctx.fillText("Type: " + this.segment.casingType.definition.replace(" Casing", ""), this.casingTypeXY.X, this.casingTypeXY.Y);
        this.canvasModel.ctx.closePath();
    }
    drawCasingSize(usingOffset: boolean = false): void {
        if (!hasCasing(this.segment)) return;
        if (usingOffset) {
            this.casingSizeXY.Y += this.labelBoxHeight + 15;
        }
        const text = `${ this.segment.casingDiameter } IN. CASING`
       
        this.canvasModel.ctx.beginPath();
        this.canvasModel.ctx.font = this.casingSizeFont;
        this.canvasModel.ctx.textAlign = "left";
        this.canvasModel.ctx.fillText(text, this.casingSizeXY.X, this.casingSizeXY.Y);
        this.canvasModel.ctx.closePath();
    }

    drawCasingDepth(usingOffset: boolean = false): void {
        if (usingOffset) {
            this.casingDepthXY.Y += this.labelBoxHeight + 15;
        }
        this.canvasModel.ctx.beginPath();
        this.canvasModel.ctx.font = this.casingSizeFont;
        this.canvasModel.ctx.textAlign = "left";
        this.canvasModel.ctx.fillText(`${this.segment.casingLength} FT. DEPTH CASING`, this.casingDepthXY.X, this.casingDepthXY.Y);
        this.canvasModel.ctx.closePath()
    }

    drawHoleSize(usingOffset: boolean = false): void {
        if (usingOffset) {
            this.boreholeSizeXY.Y += this.labelBoxHeight + 15;
        }
        this.canvasModel.ctx.beginPath();
        this.canvasModel.ctx.font = this.casingSizeFont;
        this.canvasModel.ctx.textAlign = "left";
        this.canvasModel.ctx.fillText(`${this.segment.boreholeDiameter} IN. HOLE`, this.boreholeSizeXY.X, this.boreholeSizeXY.Y);
        this.canvasModel.ctx.closePath()
    }

    drawHoleDepth(usingOffset: boolean = false): void {
        if (usingOffset) {
            this.boreholeDepthXY.Y += this.labelBoxHeight + 15;
        }
        this.canvasModel.ctx.beginPath();
        this.canvasModel.ctx.font = this.casingSizeFont;
        this.canvasModel.ctx.textAlign = "left";
        this.canvasModel.ctx.fillText(`${this.segment.boreholeDepth} FT. DEPTH HOLE`, this.boreholeDepthXY.X, this.boreholeDepthXY.Y);
        this.canvasModel.ctx.closePath()
    }

    drawCementTop(usingOffset: boolean = false): void {
        if (usingOffset) {
            this.cementTopXY.Y += this.labelBoxHeight + 15;
        }
        this.canvasModel.ctx.beginPath();
        this.canvasModel.ctx.font = this.casingSizeFont;
        this.canvasModel.ctx.textAlign = "left";
        this.canvasModel.ctx.fillText(`${this.segment.cementTop} FT. CEMENT TOP`, this.cementTopXY.X, this.cementTopXY.Y);
        this.canvasModel.ctx.closePath()
    }

    drawCementVolume(usingOffset: boolean = false): void {
        if (usingOffset) {
            this.cementVolumeXY.Y += this.labelBoxHeight + 15;
        }
        this.canvasModel.ctx.beginPath();
        this.canvasModel.ctx.font = this.casingSizeFont;
        this.canvasModel.ctx.textAlign = "left";
        this.canvasModel.ctx.fillText(`${this.segment.cementVolume} SACKS CEMENT`, this.cementVolumeXY.X, this.cementVolumeXY.Y);
        this.canvasModel.ctx.closePath()
    }

    setLeaderXY(leaderX: number, leaderY: number, right:boolean): CanvasXY {
        let startLeaderXY = new CanvasXY();

        //from end START
        switch (this.labelType) {
            case CanvasWellStringDirections.vertical:
                startLeaderXY.X = leaderX;
                startLeaderXY.Y = leaderY + this.verticalLeaderOffsetY;
                break;
            case CanvasWellStringDirections.diagonal:
                startLeaderXY.X = leaderX + 3;
                startLeaderXY.Y = leaderY + this.verticalLeaderOffsetY;
                break;
            default:
                console.log("missed case in setLeaderXY " + this.labelType);
        }
        this.leaderXYs.push(startLeaderXY);

        //VERTICAL
        let verticalLeaderXY = new CanvasXY();

        //standard vertical leader line X
        verticalLeaderXY.X = startLeaderXY.X;

        //if diagonal, make right vertical leader line angled
        if (this.labelType == CanvasWellStringDirections.diagonal && right) {
            verticalLeaderXY.X += 5;
        }
        //standard vertical leader line Y
        verticalLeaderXY.Y = startLeaderXY.Y + this.verticalLeaderLength;
        this.leaderXYs.push(verticalLeaderXY);

        //HORIZONTAL
        let horizontalLeaderXY = new CanvasXY();
        if (right) {
            horizontalLeaderXY.X = SchematicConstants.schematicRight - this.labelBoxWidth - this.verticalLeaderOffsetX;
        } else {
            horizontalLeaderXY.X = SchematicConstants.schematicLeft + this.labelBoxWidth + this.verticalLeaderOffsetX;
        }
        
        horizontalLeaderXY.Y = verticalLeaderXY.Y;
        this.leaderXYs.push(horizontalLeaderXY);

        //return the end of the leader (at box edge)
        return horizontalLeaderXY;
    }
    setNumberCircleXY(lastLeaderXY: CanvasXY, right: boolean): void {
        if (right) {
            this.numberCircleXY.X = lastLeaderXY.X + (1.5 * this.circleRadius); 
            this.numberCircleXY.Y = lastLeaderXY.Y;
        } else {
            this.numberCircleXY.X = lastLeaderXY.X - this.labelBoxWidth + (1.5 * this.circleRadius);
            this.numberCircleXY.Y = lastLeaderXY.Y;
        }
        
    }
    drawNumberCircle(usingOffset: boolean = false): void {
        if (usingOffset) {
            this.numberCircleXY.Y += this.labelBoxHeight + 15;
        }
        this.canvasModel.ctx.beginPath();
        this.canvasModel.ctx.lineWidth = this.leaderLineWidth;
        this.canvasModel.ctx.strokeStyle = this.leaderStrokeStyle;
        this.canvasModel.ctx.arc(this.numberCircleXY.X, this.numberCircleXY.Y, this.circleRadius, 0, 2 * Math.PI);
        this.canvasModel.ctx.stroke();
        this.canvasModel.ctx.closePath();
    }
    setCircleRadius(): number {
       // return ((this.labelBoxHeight - 15) / 2);
        return 15;
    }
    setSegmentNumberXY(lastLeaderXY: CanvasXY, right:boolean): void {
        if (right) {
            this.segmentNumberXY.X = lastLeaderXY.X + (1.5 * this.circleRadius);
            this.segmentNumberXY.Y = lastLeaderXY.Y + this.numberFontVerticalOffset;
        } else {
            this.segmentNumberXY.X = lastLeaderXY.X - this.labelBoxWidth + (1.5 * this.circleRadius);
            this.segmentNumberXY.Y = lastLeaderXY.Y + this.numberFontVerticalOffset;
        }
        
    }

    drawSegmentNumber(usingOffset: boolean = false): void {
        if (usingOffset) {
            this.segmentNumberXY.Y += (this.labelBoxHeight + 15);
        }
        this.canvasModel.ctx.beginPath();

        this.canvasModel.ctx.font = this.numberFont;
        this.canvasModel.ctx.textAlign = "center";
        this.canvasModel.ctx.fillText((this.segment.index + 1).toString(), this.segmentNumberXY.X, this.segmentNumberXY.Y);

        this.canvasModel.ctx.closePath();

    }

    setLabelBoxXYs(lastLeaderXY: CanvasXY, right: boolean): void {
        let halfLabelHeight = (this.labelBoxHeight / 2);
        let buffer: number = 15;
        let offsetLeader = new CanvasXY();
        offsetLeader.X = lastLeaderXY.X;
        offsetLeader.Y = lastLeaderXY.Y + this.labelBoxHeight + buffer;
        //end of Leader
        let labelBoxLeaderXY = lastLeaderXY;
        this.labelBoxXYs.push(labelBoxLeaderXY);
        this.offsetLabelBoxXYs.push(offsetLeader);
        
        if (right) {
            //TOP LEFT
            this.addLabelBoxXY(labelBoxLeaderXY.X, labelBoxLeaderXY.Y - halfLabelHeight);
            this.addLabelBoxXY(offsetLeader.X, offsetLeader.Y - halfLabelHeight, true);

            //TOP RIGHT
            this.addLabelBoxXY(labelBoxLeaderXY.X + this.labelBoxWidth, labelBoxLeaderXY.Y - halfLabelHeight);
            this.addLabelBoxXY(offsetLeader.X + this.labelBoxWidth, offsetLeader.Y - halfLabelHeight, true);
            //BOTTOM RIGHT
            this.addLabelBoxXY(labelBoxLeaderXY.X + this.labelBoxWidth, labelBoxLeaderXY.Y + halfLabelHeight);
            this.addLabelBoxXY(offsetLeader.X + this.labelBoxWidth, offsetLeader.Y + halfLabelHeight, true);
            //BOTTOM LEFT
            this.addLabelBoxXY(labelBoxLeaderXY.X, labelBoxLeaderXY.Y + halfLabelHeight);
            this.addLabelBoxXY(offsetLeader.X, offsetLeader.Y + halfLabelHeight, true);
        } else {
            //TOP RIGHT
            this.addLabelBoxXY(labelBoxLeaderXY.X, labelBoxLeaderXY.Y - halfLabelHeight);
            this.addLabelBoxXY(offsetLeader.X, offsetLeader.Y - halfLabelHeight, true);
            //TOP LEFT
            this.addLabelBoxXY(labelBoxLeaderXY.X - this.labelBoxWidth, labelBoxLeaderXY.Y - halfLabelHeight)
            this.addLabelBoxXY(offsetLeader.X - this.labelBoxWidth, offsetLeader.Y - halfLabelHeight, true);
            //BOTTOM LEFT
            this.addLabelBoxXY(labelBoxLeaderXY.X - this.labelBoxWidth, labelBoxLeaderXY.Y + halfLabelHeight);
            this.addLabelBoxXY(offsetLeader.X - this.labelBoxWidth, offsetLeader.Y + halfLabelHeight, true);

            //BOTTOM RIGHT
            this.addLabelBoxXY(labelBoxLeaderXY.X, labelBoxLeaderXY.Y + halfLabelHeight)
            this.addLabelBoxXY(offsetLeader.X, offsetLeader.Y + halfLabelHeight, true);
        }
    }
    addLabelBoxXY(X: number, Y: number, isOffset: boolean = false): void {
        let newLabelBoxXY = new CanvasXY();
        newLabelBoxXY.X = X;
        newLabelBoxXY.Y = Y;
        if (isOffset) {
            this.offsetLabelBoxXYs.push(newLabelBoxXY);
        } else {
            this.labelBoxXYs.push(newLabelBoxXY);

        }
    }

    draw(useOffset: boolean = false): void {
        this.drawLeader(useOffset);
        this.drawBox(useOffset);
        this.drawSegmentNumber(useOffset);
        this.drawNumberCircle(useOffset);
        if (this.segment.boreholeDepth != null && !isNaN(parseFloat(this.segment.boreholeDepth.toString()))) this.drawHoleDepth(useOffset);
        if (this.segment.boreholeDiameter != null && !isNaN(parseFloat(this.segment.boreholeDiameter.toString()))) this.drawHoleSize(useOffset);
        if (this.segment.casingDiameter != null && !isNaN(parseFloat(this.segment.casingDiameter.toString()))) this.drawCasingSize(useOffset);
        if (this.segment.casingLength != null && !isNaN(parseFloat(this.segment.casingLength.toString()))) this.drawCasingDepth(useOffset);
        if (this.segment.cementTop != null && !isNaN(parseFloat(this.segment.cementTop.toString()))) this.drawCementTop(useOffset);
        if (this.segment.cementVolume != null && !isNaN(parseFloat(this.segment.cementVolume.toString()))) this.drawCementVolume(useOffset);
        this.drawCasingType(useOffset);
    }
    drawLeader(useOffset: boolean = false): void {

        this.canvasModel.ctx.beginPath();
        this.canvasModel.ctx.lineWidth = this.leaderLineWidth;
        this.canvasModel.ctx.strokeStyle = this.leaderStrokeStyle;

        this.leaderXYs.forEach((leaderXY, index) => {
            if (useOffset && index > 0) {
                leaderXY.Y += this.labelBoxHeight + 15;
            }
            if (index == 0) {
                this.canvasModel.ctx.moveTo(leaderXY.X, leaderXY.Y);
            } else {
                this.canvasModel.ctx.lineTo(leaderXY.X, leaderXY.Y);
            }

        });
        this.canvasModel.ctx.stroke();
        this.canvasModel.ctx.closePath();
    }

    drawBox(useOffsetBox: boolean = false): void {
        
        this.canvasModel.ctx.beginPath();
        this.canvasModel.ctx.lineWidth = this.leaderLineWidth;
        this.canvasModel.ctx.strokeStyle = this.leaderStrokeStyle;

        if (useOffsetBox) {
            this.offsetLabelBoxXYs.forEach((labelBoxXY, index) => {
                if (index == 0) {
                    this.canvasModel.ctx.moveTo(labelBoxXY.X, labelBoxXY.Y);
                } else {
                    this.canvasModel.ctx.lineTo(labelBoxXY.X, labelBoxXY.Y);
                }
            });
        } else {
            this.labelBoxXYs.forEach((labelBoxXY, index) => {
                if (index == 0) {
                    this.canvasModel.ctx.moveTo(labelBoxXY.X, labelBoxXY.Y);
                } else {
                    this.canvasModel.ctx.lineTo(labelBoxXY.X, labelBoxXY.Y);
                }
            });
        }
        

        this.canvasModel.ctx.closePath();
        this.canvasModel.ctx.stroke();
        
    }

}

/**
 * draws ground for schematic diagram
 * */
export class CanvasSchematicGround extends CanvasModel{
    height: number = 3;
    fillStyle: string = "#000000";
    draw(): void {
        this.ctx.beginPath;
        this.ctx.fillStyle = this.fillStyle;
        this.ctx.rect(0, 0, this.canvasWidth, this.height);
        this.ctx.fill();
    }
}

export enum CanvasPolyXYLocations {
    topRight,
    bottomRight,
    bottomLeft,
    topLeft
}

export enum CanvasWellStringDirections {
    vertical,
    curve,
    diagonal,
    horizontal
}